import React, { ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useMutation, useQuery } from '@apollo/client';
import { queries, mutations } from '../api';
import { getDealFolders } from '../api/queries_v2';
import { useNotifications } from '../notifications';
import { useTeamInfo } from '../team-id-context';
import LoadingSpinner from '../components/loading-spinner';
import { CreateDealErrorMessage } from '../consts/forms-constants';
import { fillPlaceholders, paths } from '../routing';
import AddNewDealForm from './AddNewDealForm';

interface AddNewDealFormVariables {
  title: string;
  folderId?: string;
}

interface AddNewDealFormProps {
  bodyContainer: any;
  actionsContainer: any;
  renderSecondaryAction: () => ReactNode;
  currentFolderId?: string | null;
}

const schema = yup.object().shape({
  teamId: yup.string().required(),
  deal_name: yup.string().trim().required(),
  folder_id: yup.string().nullable(),
});

const AddNewDealFormik = ({ bodyContainer, actionsContainer, renderSecondaryAction, currentFolderId = null }: AddNewDealFormProps) => {
  const navigate = useNavigate();
  const { loading: teamInfoLoading, teamId, teamName } = useTeamInfo();
  const [, { error: notifyError }] = useNotifications();

  // Fetch deal folders
  const { data: foldersData, loading: foldersLoading } = useQuery(getDealFolders, {
    variables: {
      teamId,
    },
    skip: !teamId,
  });

  const [createDeal] = useMutation(mutations.createDeal, {
    refetchQueries: [
      {
        query: queries.getDealsForTeam,
        variables: {
          teamId,
          folderId: null, // Always refetch root "All Deals" view
        },
      },
      // If user is in a folder, also refetch that folder's view to update the cache
      ...(currentFolderId
        ? [
            {
              query: queries.getDealsForTeam,
              variables: {
                teamId,
                folderId: currentFolderId,
              },
            },
          ]
        : []),
      {
        query: getDealFolders,
        variables: {
          teamId,
        },
      },
    ],
  });

  const onSubmit = useCallback(
    async (values: any) => {
      try {
        const variables: AddNewDealFormVariables = {
          title: values.deal_name,
          folderId: values.folder_id || undefined,
        };

        const createdDealData = await createDeal({
          variables: { teamId: values.teamId, title: variables.title, folderId: variables.folderId },
          awaitRefetchQueries: true,
        });

        const dealId = createdDealData!.data!.createDeal!.id;
        const dealDeatilsUrl = fillPlaceholders(paths.deals.detail, {
          teamId,
          dealId,
        });

        navigate(dealDeatilsUrl);
      } catch (error) {
        notifyError({ message: CreateDealErrorMessage.CREATE_DEAL });
      }
    },
    [createDeal, navigate, notifyError, teamId]
  );

  if (teamInfoLoading || foldersLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={{
        teamId,
        deal_name: '',
        folder_id: null,
      }}
    >
      {(formikProps) => (
        <AddNewDealForm
          {...formikProps}
          bodyContainer={bodyContainer}
          actionsContainer={actionsContainer}
          renderSecondaryAction={renderSecondaryAction}
          folders={foldersData?.dealFolders?.folders || []}
        />
      )}
    </Formik>
  );
};

export default AddNewDealFormik;
